import React from 'react';
import {
  Package,
  TrendingUp,
  DollarSign,
  BarChart3,
  ShoppingCart,
  MapPin,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Activity,
  Lightbulb,
  Users,
  Target
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';
import CollapsibleSection from './CollapsibleSection';

interface NFeAnalyticsProps {
  data: {
    status_distribution: {
      authorized: number
      pending: number
      cancelled: number
      rejected: number
    }
    top_senders: Array<{
      cnpj: string
      name: string
      volume: number
      value: number
    }>
    top_customers?: Array<{
      cnpj: string
      name: string
      volume: number
      value: number
    }>
    nfe_by_state: Array<{
      state: string
      volume: number
      percentage: number
    }>
    first_five_nfes_with_occurrence: Array<{
      nfeNumber: string
      nfeSerie: string
      nfeKey: string
      occurrenceDescription: string
      carrierName: string
    }>
    monthly_cost_by_carrier: Array<Record<string, number | string>>  // {month: "Jan", Carrier1: 1000, Carrier2: 2000, ...}
    carrier_analytics?: {
      carriers: Array<{
        name: string
        total_freight: number
        total_merchandise: number
        cost_percentage: number
        total_ctes: number
        total_nfes_linked: number
        months_active: number
        avg_monthly: number
        avg_per_cte: number
        rank: number
      }>
      summary: {
        total_carriers: number
        total_freight: number
        total_merchandise: number
        overall_cost_percentage: number
        top_3_concentration: number
      }
      chart_carriers: string[]
      months: string[]
    }
    // Novos campos da IA
    value_analysis?: {
      total_value: number
      average_value: number
      median_value: number
      min_value: number
      max_value: number
      coefficient_variation: number
      distribution: Array<{
        range: string
        count: number
        percentage: number
      }>
    }
    health_score?: {
      overall_score: number
      classification: string
      color: string
      components: {
        authorization: number
        delivery: number
        rejection: number
      }
      recommendations: string[]
    }
    strategic_insights?: Array<{
      type: string
      title: string
      description: string
      impact: string
      recommendation: string
      metric_value: string
      target_value: string
    }>
    sender_concentration?: {
      top_3_percentage: number
      is_concentrated: boolean
      risk_level: string
    }
    occurrence_analysis?: {
      total_occurrences: number
      nfes_with_occurrences: number
      occurrence_rate: number
      issue_rate: number
    }
  }
}

const NFeAnalyticsSection: React.FC<NFeAnalyticsProps> = ({ data }) => {
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <CollapsibleSection
        title="Status das NFes"
        icon={BarChart3}
        defaultOpen={true}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div 
            className="p-4 rounded-lg relative overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              boxShadow: 'var(--shadow-md)'
            }}
          >
            <div className="relative flex items-center justify-between">
              <div>
                <p 
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Autorizadas
                </p>
                <p 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(data?.status_distribution?.authorized || 0)}
                </p>
              </div>
              <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
          
          <div 
            className="p-4 rounded-lg relative overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              boxShadow: 'var(--shadow-md)'
            }}
          >
            <div className="relative flex items-center justify-between">
              <div>
                <p 
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Pendentes
                </p>
                <p 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(data?.status_distribution?.pending || 0)}
                </p>
              </div>
              <div className="p-2 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
          
          <div 
            className="p-4 rounded-lg relative overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              boxShadow: 'var(--shadow-md)'
            }}
          >
            <div className="relative flex items-center justify-between">
              <div>
                <p 
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Canceladas
                </p>
                <p 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(data?.status_distribution?.cancelled || 0)}
                </p>
              </div>
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
          
          <div 
            className="p-4 rounded-lg relative overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              boxShadow: 'var(--shadow-md)'
            }}
          >
            <div className="relative flex items-center justify-between">
              <div>
                <p 
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Rejeitadas
                </p>
                <p 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(data?.status_distribution?.rejected || 0)}
                </p>
              </div>
              <div className="p-2 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 shadow-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      
      {/* se não houver ocorrências, não mostrar o card de analise de ocorrencias */}
      { data?.first_five_nfes_with_occurrence && data?.first_five_nfes_with_occurrence.length > 0 && (     
        <CollapsibleSection
          title="Análise de Ocorrências"
          icon={Package}
          defaultOpen={true}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top Occurrence Table */}
            <div>
              <h4 
                className="text-lg font-semibold mb-4 flex items-center"
                style={{ color: 'var(--text-primary)' }}
              >
                <ShoppingCart 
                  className="h-5 w-5 mr-2" 
                  style={{ color: 'var(--primary)' }}
                />
                Primeiras 5 NFEs com Ocorrência
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottomColor: 'var(--border-primary)' }}>
                      <th 
                        className="text-left py-2"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        NFe / Série
                      </th>
                      <th 
                        className="text-right py-2"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        Chave da NFe
                      </th>
                    <th 
                        className="text-right py-2"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        Descrição ocorrência
                      </th>
                      <th 
                        className="text-right py-2"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        Transportadora
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.first_five_nfes_with_occurrence || []).map((nfe, index) => (
                      <tr key={index} style={{ borderBottomColor: 'var(--border-primary)' }}>
                        <td className="py-2">
                          <div>
                            <div 
                              className="font-medium"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {nfe.nfeNumber} - {nfe.nfeSerie}
                            </div>
                            <div 
                              className="text-xs"
                              style={{ color: 'var(--text-tertiary)' }}
                            >
                              {nfe.occurrenceDescription}
                            </div>
                          </div>
                        </td>
                        <td 
                          className="text-right py-2"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {nfe.nfeKey}
                        </td>
                        <td 
                          className="text-right py-2"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {nfe.carrierName}
                        </td>
                        
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Category Breakdown Chart */}
            {/* <div>
              <h4 
                className="text-lg font-semibold mb-4 flex items-center"
                style={{ color: 'var(--text-primary)' }}
              >
                <PieChart 
                  className="h-5 w-5 mr-2" 
                  style={{ color: 'var(--primary)' }}
                />
                Distribuição por Categoria
              </h4>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={data?.product_analysis?.category_breakdown || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percentage }) => `${category} (${percentage.toFixed(1)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="quantity"
                  >
                    {(data?.product_analysis?.category_breakdown || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: '8px'
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div> */}
          </div>
        </CollapsibleSection>
      )}


      {/* Análise de Peso e Valor */}
      
      {/* <CollapsibleSection
        title="Análise de Peso e Valor"
        icon={Weight}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
         
          <div>
            <h4 
              className="text-lg font-semibold mb-4 flex items-center"
              style={{ color: 'var(--text-primary)' }}
            >
              <Weight 
                className="h-5 w-5 mr-2" 
                style={{ color: 'var(--primary)' }}
              />
              Distribuição por Peso
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Peso Total:
                </span>
                <span 
                  className="font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(data?.product_analysis?.weight_analysis?.total_weight || 0)}kg
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Peso Médio por NFe:
                </span>
                <span 
                  className="font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {(data?.product_analysis?.weight_analysis?.average_weight_per_nfe || 0).toFixed(2)}kg
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data?.product_analysis?.weight_analysis?.weight_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          
          <div>
            <h4 
              className="text-lg font-semibold mb-4 flex items-center"
              style={{ color: 'var(--text-primary)' }}
            >
              <DollarSign 
                className="h-5 w-5 mr-2" 
                style={{ color: 'var(--primary)' }}
              />
              Distribuição por Valor
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Valor Total:
                </span>
                <span 
                  className="font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatCurrency(data?.product_analysis?.value_analysis?.total_value || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Valor Médio por NFe:
                </span>
                <span 
                  className="font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatCurrency(data?.product_analysis?.value_analysis?.average_value_per_nfe || 0)}
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data?.product_analysis?.value_analysis?.value_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CollapsibleSection> */}

      {/* Custo Mensal por Transportadora */}
      <CollapsibleSection
        title="Custo Mensal por Transportadora"
        icon={DollarSign}
      >
        {/* Show content or no-data message */}
        {(!data?.monthly_cost_by_carrier || data.monthly_cost_by_carrier.length === 0) &&
         (!data?.carrier_analytics?.carriers || data.carrier_analytics.carriers.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <DollarSign size={48} className="mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <p className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
              Nenhum dado de custo por transportadora disponível para o período selecionado.
            </p>
            <p className="text-center text-xs mt-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
              Verifique se existem CTEs vinculados às NFes no período.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Total Frete
                </div>
                <div className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  R$ {(data?.carrier_analytics?.summary?.total_freight || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Total Mercadoria
                </div>
                <div className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  R$ {(data?.carrier_analytics?.summary?.total_merchandise || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
                  % Custo/Mercadoria
                </div>
                <div className="text-xl font-bold" style={{ color: (data?.carrier_analytics?.summary?.overall_cost_percentage || 0) > 10 ? '#ef4444' : '#22c55e' }}>
                  {(data?.carrier_analytics?.summary?.overall_cost_percentage || 0).toFixed(2)}%
                </div>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Transportadoras
                </div>
                <div className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {data?.carrier_analytics?.summary?.total_carriers || 0}
                </div>
              </div>
            </div>

            {/* Vertical Bar Chart - Custo por Transportadora */}
            {data?.carrier_analytics?.carriers && data.carrier_analytics.carriers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Custo Total por Transportadora
                </h4>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={data.carrier_analytics.carriers.slice(0, 10).map(c => ({
                      name: c.name.length > 15 ? c.name.substring(0, 15) + '...' : c.name,
                      fullName: c.name,
                      frete: c.total_freight,
                      mercadoria: c.total_merchandise,
                      percentual: c.cost_percentage
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      width={95}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '8px'
                      }}
                      formatter={(value: number, name: string) => [
                        `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                        name === 'frete' ? 'Frete' : 'Mercadoria'
                      ]}
                      labelFormatter={(label) => {
                        const item = data.carrier_analytics?.carriers?.find(c =>
                          c.name.startsWith(label.replace('...', ''))
                        );
                        return item?.name || label;
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="frete" name="Frete" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="mercadoria" name="Mercadoria" fill="#22c55e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Stacked Bar Chart - Monthly */}
            {data?.monthly_cost_by_carrier && data.monthly_cost_by_carrier.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Custo de Frete por Mês
                </h4>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={data.monthly_cost_by_carrier}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '8px'
                      }}
                      formatter={(value: number, name: string) => [
                        `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                        name
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    {(data?.carrier_analytics?.chart_carriers || []).slice(0, 8).map((carrier, index) => {
                      const colors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
                      return (
                        <Bar
                          key={carrier}
                          dataKey={carrier}
                          stackId="a"
                          fill={colors[index % colors.length]}
                          name={carrier.length > 20 ? carrier.substring(0, 20) + '...' : carrier}
                        />
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Line Chart - Trend Evolution */}
            {data?.monthly_cost_by_carrier && data.monthly_cost_by_carrier.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Evolução por Transportadora
                </h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.monthly_cost_by_carrier}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '8px'
                      }}
                      formatter={(value: number, name: string) => [
                        `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                        name
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    {(data?.carrier_analytics?.chart_carriers || []).slice(0, 5).map((carrier, index) => {
                      const colors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];
                      return (
                        <Line
                          key={carrier}
                          type="monotone"
                          dataKey={carrier}
                          stroke={colors[index % colors.length]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name={carrier.length > 20 ? carrier.substring(0, 20) + '...' : carrier}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Carrier Ranking Cards */}
            {data?.carrier_analytics?.carriers && data.carrier_analytics.carriers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Ranking de Transportadoras
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {data.carrier_analytics.carriers.slice(0, 8).map((carrier, index) => {
                    const rankColors = ['#fbbf24', '#9ca3af', '#cd7f32'];
                    const rankBg = index < 3 ? rankColors[index] : '#6b7280';

                    return (
                      <div
                        key={carrier.name}
                        className="p-4 rounded-lg relative overflow-hidden"
                        style={{ backgroundColor: 'var(--bg-secondary)' }}
                      >
                        {/* Rank Badge */}
                        <div
                          className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: rankBg }}
                        >
                          {carrier.rank}
                        </div>

                        {/* Carrier Name */}
                        <div
                          className="font-semibold text-sm mb-3 pr-10 truncate"
                          style={{ color: 'var(--text-primary)' }}
                          title={carrier.name}
                        >
                          {carrier.name}
                        </div>

                        {/* Metrics */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Total Frete</span>
                            <span className="text-sm font-semibold" style={{ color: '#3b82f6' }}>
                              R$ {carrier.total_freight.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Mercadoria</span>
                            <span className="text-sm font-semibold" style={{ color: '#22c55e' }}>
                              R$ {carrier.total_merchandise.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>% Custo</span>
                            <span
                              className="text-sm font-semibold"
                              style={{ color: carrier.cost_percentage > 10 ? '#ef4444' : '#22c55e' }}
                            >
                              {carrier.cost_percentage.toFixed(2)}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>CTEs</span>
                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                              {carrier.total_ctes}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Média/CTE</span>
                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                              R$ {carrier.avg_per_cte.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </CollapsibleSection>

      {/* Geographic Distribution */}
      <CollapsibleSection
        title="Distribuição Geográfica"
        icon={MapPin}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              Por Estado
            </h4>
            {/* Gráfico de Pizza */}
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={(data?.nfe_by_state || []).map((state) => ({
                      name: state.state === 'EX' ? 'Exportação' : state.state,
                      value: state.volume,
                      percentage: state.percentage
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {(data?.nfe_by_state || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)'
                    }}
                    formatter={(value: number, name: string, props: any) => [
                      `${formatNumber(value)} documentos (${(props.payload.percentage || 0).toFixed(1)}%)`,
                      name
                    ]}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            {/* Legenda */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {(data?.nfe_by_state || []).slice(0, 8).map((state, index) => (
                <div key={index} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span
                      className="text-xs font-medium"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {state.state === 'EX' ? 'Exportação' : state.state}
                    </span>
                  </div>
                  <span
                    className="text-xs font-semibold"
                    style={{ color: COLORS[index % COLORS.length] }}
                  >
                    {(state.percentage || 0).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              Top 10 Clientes por Valor
            </h4>
            {(() => {
              const customersData = (data?.top_customers || data?.top_senders || [])
                .slice(0, 10)
                .filter(c => c && c.name && (c.value || 0) > 0)
                .map((customer, index) => ({
                  name: (customer.name || '').length > 18
                    ? (customer.name || '').substring(0, 18) + '...'
                    : (customer.name || 'N/A'),
                  fullName: customer.name || 'N/A',
                  value: customer.value || 0,
                  fill: COLORS[index % COLORS.length]
                }));

              if (customersData.length === 0) {
                return (
                  <div
                    className="h-80 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: 'var(--bg-tertiary)' }}
                  >
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Nenhum dado de cliente disponível
                    </p>
                  </div>
                );
              }

              return (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={customersData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                      <XAxis
                        type="number"
                        tickFormatter={(value) => {
                          if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
                          if (value >= 1000) return `R$ ${(value / 1000).toFixed(0)}K`;
                          return `R$ ${value.toFixed(0)}`;
                        }}
                        stroke="var(--text-secondary)"
                        fontSize={11}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        stroke="var(--text-secondary)"
                        fontSize={10}
                        width={120}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--bg-card)',
                          border: '1px solid var(--border-primary)',
                          borderRadius: '8px',
                          color: 'var(--text-primary)'
                        }}
                        formatter={(value: number) => [formatCurrency(value), 'Valor Total']}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {customersData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </div>
        </div>
      </CollapsibleSection>

      {/* Health Score e Insights Estratégicos */}
      {(data?.health_score || data?.strategic_insights) && (
        <CollapsibleSection
          title="Análise Inteligente de NFEs"
          icon={Activity}
          defaultOpen={true}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Health Score Card */}
            {data?.health_score && (
              <div
                className="p-6 rounded-xl relative overflow-hidden"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4
                    className="text-lg font-semibold flex items-center"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Activity className="h-5 w-5 mr-2" style={{ color: 'var(--primary)' }} />
                    Health Score
                  </h4>
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: data.health_score.color === 'green' ? 'rgba(34, 197, 94, 0.1)' :
                                      data.health_score.color === 'blue' ? 'rgba(59, 130, 246, 0.1)' :
                                      data.health_score.color === 'yellow' ? 'rgba(234, 179, 8, 0.1)' :
                                      'rgba(239, 68, 68, 0.1)',
                      color: data.health_score.color === 'green' ? '#22c55e' :
                             data.health_score.color === 'blue' ? '#3b82f6' :
                             data.health_score.color === 'yellow' ? '#eab308' : '#ef4444'
                    }}
                  >
                    {data.health_score.classification}
                  </span>
                </div>

                {/* Score Circle */}
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="relative w-32 h-32 rounded-full flex items-center justify-center"
                    style={{
                      background: `conic-gradient(${
                        data.health_score.color === 'green' ? '#22c55e' :
                        data.health_score.color === 'blue' ? '#3b82f6' :
                        data.health_score.color === 'yellow' ? '#eab308' : '#ef4444'
                      } ${(data.health_score.overall_score || 0) * 3.6}deg, var(--bg-tertiary) 0deg)`
                    }}
                  >
                    <div
                      className="absolute w-24 h-24 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--bg-card)' }}
                    >
                      <span
                        className="text-3xl font-bold"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {Math.round(data.health_score.overall_score || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Components */}
                {data.health_score.components && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Autorização</span>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {Math.round(data.health_score.components.authorization || 0)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Entregas</span>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {Math.round(data.health_score.components.delivery || 0)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Rejeições</span>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {Math.round(data.health_score.components.rejection || 0)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Value Analysis Card */}
            {data?.value_analysis && (
              <div
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
                <h4
                  className="text-lg font-semibold mb-4 flex items-center"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <DollarSign className="h-5 w-5 mr-2" style={{ color: 'var(--primary)' }} />
                  Análise de Valores
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total</span>
                    <span className="text-sm font-bold" style={{ color: '#22c55e' }}>
                      {formatCurrency(data.value_analysis.total_value || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Média</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(data.value_analysis.average_value || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Mediana</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(data.value_analysis.median_value || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Mínimo</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(data.value_analysis.min_value || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Máximo</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(data.value_analysis.max_value || 0)}
                    </span>
                  </div>
                  <div className="pt-2 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Variação</span>
                      <span
                        className="text-sm font-medium"
                        style={{
                          color: (data.value_analysis.coefficient_variation || 0) > 50 ? '#f59e0b' : '#22c55e'
                        }}
                      >
                        {(data.value_analysis.coefficient_variation || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sender Concentration Card */}
            {data?.sender_concentration && (
              <div
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
                <h4
                  className="text-lg font-semibold mb-4 flex items-center"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <Users className="h-5 w-5 mr-2" style={{ color: 'var(--primary)' }} />
                  Concentração de Remetentes
                </h4>
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="text-4xl font-bold"
                    style={{
                      color: data.sender_concentration.risk_level === 'high' ? '#ef4444' :
                             data.sender_concentration.risk_level === 'medium' ? '#f59e0b' : '#22c55e'
                    }}
                  >
                    {(data.sender_concentration.top_3_percentage || 0).toFixed(1)}%
                  </div>
                </div>
                <p
                  className="text-center text-sm mb-4"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  dos documentos concentrados nos 3 maiores remetentes
                </p>
                <div
                  className="flex items-center justify-center p-2 rounded-lg"
                  style={{
                    backgroundColor: data.sender_concentration.risk_level === 'high' ? 'rgba(239, 68, 68, 0.1)' :
                                    data.sender_concentration.risk_level === 'medium' ? 'rgba(245, 158, 11, 0.1)' :
                                    'rgba(34, 197, 94, 0.1)'
                  }}
                >
                  {data.sender_concentration.is_concentrated ? (
                    <AlertTriangle
                      className="h-4 w-4 mr-2"
                      style={{ color: data.sender_concentration.risk_level === 'high' ? '#ef4444' : '#f59e0b' }}
                    />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" style={{ color: '#22c55e' }} />
                  )}
                  <span
                    className="text-sm font-medium"
                    style={{
                      color: data.sender_concentration.risk_level === 'high' ? '#ef4444' :
                             data.sender_concentration.risk_level === 'medium' ? '#f59e0b' : '#22c55e'
                    }}
                  >
                    Risco {data.sender_concentration.risk_level === 'high' ? 'Alto' :
                           data.sender_concentration.risk_level === 'medium' ? 'Médio' : 'Baixo'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Strategic Insights */}
          {data?.strategic_insights && data.strategic_insights.length > 0 && (
            <div className="mt-6">
              <h4
                className="text-lg font-semibold mb-4 flex items-center"
                style={{ color: 'var(--text-primary)' }}
              >
                <Lightbulb className="h-5 w-5 mr-2" style={{ color: '#f59e0b' }} />
                Insights Estratégicos
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.strategic_insights.map((insight, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: insight.impact === 'high' ? 'rgba(239, 68, 68, 0.05)' :
                                      insight.impact === 'medium' ? 'rgba(245, 158, 11, 0.05)' :
                                      insight.impact === 'positive' ? 'rgba(34, 197, 94, 0.05)' :
                                      'rgba(59, 130, 246, 0.05)',
                      border: `1px solid ${
                        insight.impact === 'high' ? 'rgba(239, 68, 68, 0.2)' :
                        insight.impact === 'medium' ? 'rgba(245, 158, 11, 0.2)' :
                        insight.impact === 'positive' ? 'rgba(34, 197, 94, 0.2)' :
                        'rgba(59, 130, 246, 0.2)'
                      }`
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className="p-2 rounded-lg"
                        style={{
                          backgroundColor: insight.impact === 'high' ? 'rgba(239, 68, 68, 0.1)' :
                                          insight.impact === 'medium' ? 'rgba(245, 158, 11, 0.1)' :
                                          insight.impact === 'positive' ? 'rgba(34, 197, 94, 0.1)' :
                                          'rgba(59, 130, 246, 0.1)'
                        }}
                      >
                        {insight.type === 'quality' ? <CheckCircle className="h-5 w-5" style={{ color: insight.impact === 'positive' ? '#22c55e' : '#ef4444' }} /> :
                         insight.type === 'operational' ? <Target className="h-5 w-5" style={{ color: '#f59e0b' }} /> :
                         insight.type === 'financial' ? <DollarSign className="h-5 w-5" style={{ color: '#3b82f6' }} /> :
                         <Lightbulb className="h-5 w-5" style={{ color: '#8b5cf6' }} />}
                      </div>
                      <div className="flex-1">
                        <h5
                          className="font-semibold text-sm mb-1"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {insight.title}
                        </h5>
                        <p
                          className="text-sm mb-2"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          {insight.description}
                        </p>
                        <div className="flex items-center justify-between text-xs">
                          <span
                            className="px-2 py-1 rounded"
                            style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              color: 'var(--text-secondary)'
                            }}
                          >
                            Atual: {insight.metric_value}
                          </span>
                          <span
                            className="px-2 py-1 rounded"
                            style={{
                              backgroundColor: 'rgba(34, 197, 94, 0.1)',
                              color: '#22c55e'
                            }}
                          >
                            Meta: {insight.target_value}
                          </span>
                        </div>
                        <p
                          className="text-xs mt-2 italic"
                          style={{ color: 'var(--text-tertiary)' }}
                        >
                          {insight.recommendation}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations from Health Score */}
          {data?.health_score?.recommendations && data.health_score.recommendations.length > 0 && (
            <div className="mt-6">
              <h4
                className="text-lg font-semibold mb-3 flex items-center"
                style={{ color: 'var(--text-primary)' }}
              >
                <Target className="h-5 w-5 mr-2" style={{ color: '#22c55e' }} />
                Recomendações
              </h4>
              <div className="space-y-2">
                {data.health_score.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 rounded-lg"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-primary)'
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-3" style={{ color: '#22c55e' }} />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {rec}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleSection>
      )}
    </div>
  );
};

export default NFeAnalyticsSection;
